import React, { useEffect } from 'react'
import posed, { PoseGroup } from 'react-pose'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import {
    useEditMode,
	useMyFieldPositions,
	useMyLocks,
	useMyNumPeriods,
	useMyPlayerNames,
	useReady,
	useSetAmDragging,
    useShowNetStats,
	useShowNetStatsCombos,
	useUserHasPro,
} from './data'
import GridCellPosition from './GridCellPosition'
import GridCell from './GridCell'
import PlayerName from './PlayerName'
import { BENCHED } from './definitions'
import { DraggableItem, FixedItem } from './definitionsPoses'
import { formatPeriodIndex } from './periodLabels'
import { RxDragHandleDots2 } from 'react-icons/rx'
import { FaArrowRightLong } from 'react-icons/fa6'
import type { SubstitutionDragState } from './useSubstitutionDrag'
import { canDragPositionTile } from './positionTileDrag'

type GridPositionsProps = {
	best: Best
	data: AllRosterStats | undefined | null
	onDrop: (e: React.DragEvent, obj: OnDropObject, isQtr?: boolean) => void
	qtr: number
	showAltBoard: boolean
	numPlayersShown: number
	onCourtPlayers: PlayerId[]
	endRoster: RosterData
	substitutionDrag: SubstitutionDragState | null
	recentSubstitutionIndexes: number[]
	pendingSubstitutionReturnIndex: number | null
	gentleSubstitutionSettleIndex: number | null
	legacySubHelpIndex: number | null
	onSubstitutionPointerDown: (i: I, event: React.PointerEvent<HTMLDivElement>, label: string, positionName: string) => void
	onNormalDragStart: () => void
}

const GridPositions = (props: GridPositionsProps) => {

	const { best, data, onDrop, qtr, showAltBoard } = props

    const { players, roster, nulled, available } = best

	const ready = useReady()
    const editMode = useEditMode()
    const showNetStats = useShowNetStats()
	const showNetStatsCombos = useShowNetStatsCombos()
	const userHasPro = useUserHasPro()
	const setAmDragging = useSetAmDragging()
	const numPeriods = useMyNumPeriods()
	const fieldPositions = useMyFieldPositions()
	const playerNames = useMyPlayerNames()
	const normalDragElement = React.useRef<HTMLElement | null>(null)
	const normalDragStartPosition = React.useRef<{ x: number, y: number } | null>(null)
	// Pose keys are roster-slot indexes. A split can make one of those slots hidden or
	// zero-height, so reusing its measurement later may send a tile flying in from the
	// top-left. Keep track of split slots across every intermediate GO render.
	const previousTransitionIndexes = React.useRef<Set<number>>(new Set())
	const previousHadRoster = React.useRef<boolean | null>(null)
	const hasEverHadRoster = React.useRef(false)
	const suppressedFlipIndexes = React.useRef<Set<number>>(new Set())
	const [ _, releaseSuppressedFlips ] = React.useReducer(value => value + 1, 0)

    const locks = useMyLocks()
	const effectiveEndRoster = props.endRoster.length === roster.length ? props.endRoster : roster
	const transitionIndexes = queryQuarterTransitionIndexes(roster, effectiveEndRoster, qtr, numPeriods)
	const hasRoster = !!roster.length
	const crossedExistingRosterBoundary = previousHadRoster.current !== null && (
		(previousHadRoster.current && !hasRoster)
		|| (!previousHadRoster.current && hasRoster && hasEverHadRoster.current)
	)

	// During GO, suppress only split/sub slots so ordinary tiles still shuffle. On
	// erase and subsequent restore, suppress every tile: blank tiles should change
	// in place. Do not treat the first-ever roster as a restore, because its entrance
	// animation is intentional.
	if ((!ready && previousHadRoster.current) || crossedExistingRosterBoundary) {
		previousTransitionIndexes.current.forEach(index => suppressedFlipIndexes.current.add(index))
		transitionIndexes.forEach(index => suppressedFlipIndexes.current.add(index))
	}
	const suppressedFlipSignature = Array.from(suppressedFlipIndexes.current).sort((a, b) => a - b).join(',')

	useEffect(() => {
		if (hasRoster) previousTransitionIndexes.current = transitionIndexes
		previousHadRoster.current = hasRoster
		if (hasRoster) hasEverHadRoster.current = true
	})

	useEffect(() => {
		if (!ready || !suppressedFlipIndexes.current.size) return

		// Retain suppression through Pose's measurement/render pass. Releasing it on
		// the next frame is safe because the posed component itself remains mounted.
		const frame = requestAnimationFrame(() => {
			suppressedFlipIndexes.current.clear()
			releaseSuppressedFlips()
		})
		return () => cancelAnimationFrame(frame)
	}, [ ready, suppressedFlipSignature ])

	if (!data)
		return null

    // console.log('GridPositions qtr', qtr, 'with data', data, 'and locks', locks)

	const list: Array<{
		player: PlayerId
		position: string | undefined
		positionName: string
		content: string | React.ReactNode
		isNullPosition: boolean
		isLocked: boolean
		isAvailable: boolean
		netStatElement: React.ReactNode | null
		pairElement: React.ReactNode | null
		trioElement: React.ReactNode | null
		noRoster: boolean
		i: I
		key: string | number
		onDragEnd: (e: React.DragEvent) => void
		endRosterIndex: number
		isTransitionChanged: boolean
		endPositionName: string
		endPlayerInStartSlot: PlayerId
		endPlayerStartPositionName: string
	}> = [ ]
	const playersPerQtr = roster.length / numPeriods

	players.forEach(player => {
		const bit = data[player][qtr]
        if (!bit) {
			throw Error("Missing data for player, about to crash... player" + player + "qtr" + qtr + "data" + data + "best" + best)
        }

		const i: I | undefined = data[player][qtr]?.i
		if (i === undefined) {
			throw Error("Why is i undefined?... player" + player + "qtr" + qtr + "data" + data + "best" + best)
		}

		const quarterStart = qtr * playersPerQtr
		const quarterEnd = quarterStart + playersPerQtr
		const endIndex = effectiveEndRoster.findIndex((endPlayer, index) => (
			index >= quarterStart && index < quarterEnd && endPlayer === player
		))
		const endRosterIndex = endIndex === -1 ? Number(i) : endIndex
		const startPositionName = fieldPositions[Number(i) % playersPerQtr] || BENCHED
		const endPositionName = fieldPositions[endRosterIndex % playersPerQtr] || BENCHED
		const endPlayerInStartSlot = effectiveEndRoster[Number(i)]
		const endPlayerStartIndex = roster.findIndex((startPlayer, index) => (
			index >= quarterStart && index < quarterEnd && startPlayer === endPlayerInStartSlot
		))
		const endPlayerStartPositionName = fieldPositions[endPlayerStartIndex % playersPerQtr] || BENCHED
		const isTransitionChanged = endPositionName !== startPositionName
		const noRoster = !roster.length

		let position = data[player][qtr]?.position
		let positionName: FieldPosition = startPositionName
		if (noRoster) {
			positionName = BENCHED
		}

		const isNullPosition = roster.length ? nulled.includes(bit.i) : false
		const netStatPositionNames = showAltBoard ? [ positionName ] : [ positionName, endPositionName ]
		const netStatPositionName = netStatPositionNames.find(positionName => positionName !== BENCHED) || null
		const netStatElement = (showNetStats && netStatPositionName) ? (
			<NetStatValue
				value={bit.netStatContribution ?? bit.netStat}
				position={netStatPositionName}
				isNullPosition={isNullPosition}
			/>
		) : null

		const isAvailable = available[player]

		const showComboElement = positionName !== BENCHED

		const pairElement = (showAltBoard && showNetStatsCombos && userHasPro && showComboElement && bit.pair) ? (
			<PairElement
				position={positionName}
				value={bit.pair}
			/>
		) : null

		const trioElement = (showAltBoard && showNetStatsCombos && userHasPro && showComboElement && bit.trio) ? (
			<PairElement
				position={positionName}
				value={bit.trio}
				offset={bit.trioIndex}
				isTrio={true}
			/>
		) : null

        // console.log('player', player, roster.length, i, qtr, data[player], data[player][qtr], positionName, 'trioElement', trioElement)

		const key = i

		const onDragEnd = (e: React.DragEvent) => {
			console.log('GridPositions onDragEnd - column player ', player, 'position', positionName, 'qtr', qtr, 'i', i)
			const endPosition = eventPosition(e)
			const moved = !!(normalDragStartPosition.current && endPosition && Math.hypot(
				endPosition.x - normalDragStartPosition.current.x,
				endPosition.y - normalDragStartPosition.current.y,
			) > 8)
			normalDragStartPosition.current = null
			normalDragElement.current?.classList.remove('position-drag-lifted')
			normalDragElement.current = null
			// This is necessary to prevent weird double-triggering on Mobile Web.
			e.preventDefault()
			return onDrop(e, { i, moved })
		}

		let content
		if (noRoster) {
			content = ''
		} else if (showAltBoard) {
			content = (
				<PlayerName playerId={player} subPlayerId={endPlayerInStartSlot !== player ? endPlayerInStartSlot : null} />
			)
		} else {
			content = (
				<PositionTransitionName
					startPosition={positionName}
					endPosition={endPositionName}
					forceSplit={props.substitutionDrag?.sourceIndex === endRosterIndex || props.pendingSubstitutionReturnIndex === endRosterIndex}
				/>
			)
		}

		const isLocked = typeof i === 'number' && locks[i]

		list.push({
			player,
			position,
			positionName,
			content,
			isLocked,
			isNullPosition,
			isAvailable,
			netStatElement,
			pairElement,
			trioElement,
			noRoster,
			i,
			key,
			onDragEnd,
			endRosterIndex: showAltBoard ? Number(i) : endRosterIndex,
			isTransitionChanged,
			endPositionName,
			endPlayerInStartSlot,
			endPlayerStartPositionName,
		})
	})

	if (showAltBoard) {
		list.sort((a, b) => Number(a.i) - Number(b.i))
	}
    // console.log('list', qtr, list.length, list)

	const allowDrag = (editMode && ready && roster.length) ? true : false

	const onDragStart = (event: React.DragEvent) => {
		console.log('onDragStart')
		normalDragStartPosition.current = eventPosition(event)
		normalDragElement.current?.classList.remove('position-drag-lifted')
		if (event.currentTarget instanceof HTMLElement) {
			normalDragElement.current = event.currentTarget
			normalDragElement.current.classList.add('position-drag-lifted')
		}
		props.onNormalDragStart()
		setAmDragging(true)
		Haptics.impact({ style: ImpactStyle.Light })
	}

	let contents = list.map((item, index) => {
		// Item shouldn't be draggable unless we need it, because otherwise that makes
		// it challenging to scroll (everything moves so there's nowhere safe to put
		// your finger).
		const isDraggable = canDragPositionTile({
			allowDrag,
			isAvailable: item.isAvailable,
			position: item.position,
			endPosition: item.endPositionName,
			isLocked: item.isLocked,
			isNullPosition: item.isNullPosition,
		})
		const isSubstitutionSource = props.substitutionDrag?.sourceIndex === item.endRosterIndex
		const suppressFlip = crossedExistingRosterBoundary || suppressedFlipIndexes.current.has(Number(item.i))
		const Element = isDraggable && !isSubstitutionSource ? DraggableItem : FixedItem

		const myClasses = [ ]
		if (allowDrag) {
			myClasses.push('posed-element')
			if (!isDraggable) {
				myClasses.push('not-draggable')
			}
		} else {
			myClasses.push('fixed-element')
			if (!item.isAvailable) {
				myClasses.push('fixed-element-unavailable')
			}
			if (item.positionName === BENCHED) {
				myClasses.push('fixed-element--')
			}
		}
		if (
			(showAltBoard && !available[item.player] && !props.onCourtPlayers.includes(item.player))
				||
			(!showAltBoard && index >= props.numPlayersShown)
		) {
			// Players are hidden if:
			// 1. We're showing the alt board and the player is both unavailable & never on court
			// 2. We're NOT showing the alt board but the player is beyond the number of players
			//    that Roster.tsx says we should show (numPlayersShown).
			myClasses.push('hide')
		}
		const cellClasses = [ ]
		const isPendingReturn = props.pendingSubstitutionReturnIndex === item.endRosterIndex
		if (item.netStatElement) cellClasses.push('cell-has-netstat')
		if (item.isTransitionChanged) cellClasses.push('cell-substitution-changed')
		if (showAltBoard && item.isTransitionChanged) cellClasses.push('cell-substatus-nonzero')
		if (isSubstitutionSource || isPendingReturn) cellClasses.push('cell-substitution-source')
		if (props.recentSubstitutionIndexes.includes(item.endRosterIndex)) cellClasses.push('cell-substitution-recent')
		if (props.gentleSubstitutionSettleIndex === item.endRosterIndex) cellClasses.push('cell-substitution-recent-gentle')
		const isShowingLegacySubHelp = props.legacySubHelpIndex === Number(item.i)

		return (
			<Element
				key={item.key}
				className={myClasses.join(' ')}
				poseSuppressFlip={suppressFlip}
				onDragStart={onDragStart}
				onDragEnd={item.onDragEnd}
			>
				<GridCellPosition
					i={item.i}
					content={item.content}
					positionName={item.positionName}
					isNullPosition={item.isNullPosition}
					isAvailable={item.isAvailable}
					isLocked={item.isLocked}
					isSubSplitting={isShowingLegacySubHelp}
					subStatus={0}
					className={cellClasses.join(' ')}
					endRosterIndex={item.endRosterIndex}
					onPointerDown={(_i, event) => {
						props.onSubstitutionPointerDown(
							item.endRosterIndex,
							event,
							showAltBoard ? (playerNames?.[item.endPlayerInStartSlot] || item.endPlayerInStartSlot) : item.endPositionName,
							showAltBoard ? item.endPlayerStartPositionName : item.endPositionName,
						)
					}}
				>
					{item.netStatElement}
					{item.pairElement}
					{item.trioElement}
				</GridCellPosition>
			</Element>
		)})

		return (
			<div className="column column-positions">
				<GridPositionsHeader
					allowDrag={allowDrag}
					qtr={qtr}
					onDrop={onDrop}
					onDragStart={props.onNormalDragStart}
				/>
				<div className="column-positions-without-header">
					<PoseGroup>
						{contents}
					</PoseGroup>
				</div>
			</div>
		)
}

function queryQuarterTransitionIndexes(
	roster: RosterData,
	endRoster: RosterData,
	qtr: number,
	numPeriods: number,
) {
	const indexes = new Set<number>()
	if (!roster.length || roster.length !== endRoster.length) return indexes

	const playersPerQtr = roster.length / numPeriods
	const quarterStart = qtr * playersPerQtr
	const quarterEnd = quarterStart + playersPerQtr
	for (let index = quarterStart; index < quarterEnd; index += 1) {
		if (roster[index] !== endRoster[index]) indexes.add(index)
	}
	return indexes
}

function eventPosition(event: React.DragEvent) {
	if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
		return { x: event.clientX, y: event.clientY }
	}

	const touch = (event as unknown as TouchEvent).changedTouches?.[0]
	return touch ? { x: touch.clientX, y: touch.clientY } : null
}

type GridPositionsHeaderProps = {
	allowDrag: boolean
	qtr: number
	onDrop: (e: React.DragEvent, obj: OnDropObject, isQtr?: boolean) => void
	onDragStart: () => void
}

const GridPositionsHeader = (props: GridPositionsHeaderProps) => {

	const { allowDrag, qtr } = props

	const setAmDragging = useSetAmDragging()
	const numPeriods = useMyNumPeriods()

	const key = `qtr-header-${qtr}`
	const content = formatPeriodIndex(qtr, numPeriods)

	if (!allowDrag) {
		return (
			<FixedItem className="fixed-element" key={key}>
				<GridCell
					type="header"
					content={content}
				/>
			</FixedItem>
		)
	}

	const DraggableQtr = posed.div({
		draggable: 'x',
		init: { scale: 1 },
		drag: { scale: 1.1, left:'-5%' },
	})

	const onDragStart = () => {
		props.onDragStart()
		Haptics.impact({ style: ImpactStyle.Light })
		setAmDragging(true)
	}

	const onDragEnd = (e: React.DragEvent) => {
		console.log('onDragEnd - column header qtr ', qtr)
		return props.onDrop(e, { qtr }, true)
	}

	return (
		<DraggableQtr
			key={key}
			className="posed-element"
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
		>
			<GridCell
				type="header"
				qtr={qtr}
				content={
					<>
						<RxDragHandleDots2 className="drag-handle" />
						{content}
					</>
				}
			/>
		</DraggableQtr>
	)
}

type PositionNameProps = {
	positionName: FieldPosition
	subStatus: SubStatus
}

type PositionTransitionNameProps = {
	startPosition: string
	endPosition: string
	forceSplit?: boolean
}

export const PositionTransitionName = (props: PositionTransitionNameProps) => {
	if (props.startPosition === props.endPosition && !props.forceSplit) {
		return <>{props.startPosition}</>
	}

	return (
		<span className="substitution-position-transition">
			<span className={`substitution-position-half substitution-position-start cell-position-${props.startPosition}`}>
				{props.startPosition}
			</span>
			<FaArrowRightLong className="icon-sub" />
			<span className={`substitution-position-half substitution-position-end cell-position-${props.endPosition}`}>
				{props.endPosition}
			</span>
		</span>
	)
}

export const PositionName = (props: PositionNameProps) => {

	const { positionName, subStatus } = props

	if (subStatus === 1) {
		return (
			<>
				{positionName}
				<FaArrowRightLong className="icon-sub" />
			</>
		)
	} else if (subStatus === 2) {
		return (
			<>
				<FaArrowRightLong className="icon-sub" />
				{positionName}
			</>
		)
	} else {
		return (
			<>
				{positionName}
			</>
		)
	}
}

type NetStatValueProps = {
	value: number | null | undefined
	position: string
	isNullPosition?: boolean
}

const NetStatValue = (props: NetStatValueProps) => {

	if (props.value === null || props.value === undefined)
		return null

	let str = Number.parseFloat(String(props.value)).toFixed(1)

    if (str === '-0.0') {
        str = '0.0'
    }

    let myClass
    if (str.charAt(0) === '-') {
        myClass = 'negative'
    } else {
		str = `+${str}`
        myClass = 'positive'
	}

	return (
		<div className={`netStatValue netStatValue-${myClass} cell-position-${props.position}`}>
			{str}
		</div>
	)
}

type PairElementProps = {
	value: number
	position: string
	offset?: number
	isTrio?: boolean
}

const PairElement = (props: PairElementProps) => {
	return (
		<div className={`pair-element ${props.isTrio ? 'trio-element' : ''} ${props.offset ? 'trio-element-offset-' + props.offset : ''}`}>
			<NetStatValue position={props.position} value={props.value} />
		</div>
	)
}

export default GridPositions
